import { createFileRoute } from "@tanstack/react-router";
import type { ClientLocationBaseData } from "api-types/locations/locationsResponses";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import styles from "lib/components/builders/communities/lots/LotDetails.module.scss";
import LotDetailsForm from "lib/components/builders/communities/lots/LotDetailsForm";
import getLotNumberDisplay from "lib/components/builders/communities/plans/getLotNumberDisplay";
import { getEffectiveLocationAddress } from "lib/components/builders/communities/utils/locationUtils";
import { shouldDisplayPricing } from "lib/components/builders/communities/utils/pricingVisibility";
import { useLocationHierarchy } from "lib/components/builders/communities/utils/useLocationHierarchy";
import { getBuilderLocationBaseDataURL, getBuilderLotURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { locationHomePlansPath, locationLotsPath, useAppRouterParams } from "lib/routeHelpers";
import type { LotRowData } from "lib/state/LotsContext";
import { LotDetailsContext } from "lib/state/LotsContext";
import type { ReactNode } from "react";
import Loading from "ui/components/Loading";
import withAuthentication from "../../../../../../../components/withAuthentication";

function LotPage() {
  const { builderId, lotId, locationId } = useAppRouterParams();

  if (!builderId || !lotId || !locationId) {
    return null;
  }

  return <ReadyLotPage builderId={builderId} lotId={lotId} locationId={locationId} />;
}

interface ReadyLotPageProps {
  builderId: string;
  lotId: string;
  locationId: string;
}

export function ReadyLotPage({ builderId, lotId, locationId }: ReadyLotPageProps): ReactNode {
  const { data: locationHierarchy } = useLocationHierarchy(builderId, locationId);

  const { loading: locationDataLoading, data: locationData } = useFetch<ClientLocationBaseData>(
    getBuilderLocationBaseDataURL({ builderId, locationId }),
    [builderId, locationId],
  );
  const { loading: lotRowDataLoading, data: lot } = useFetch<LotRowData>(
    getBuilderLotURL({ builderId, locationId, lotId }),
    [builderId, locationId, lotId],
  );
  // Address inheritance is anchored on the lot's immediate parent location (lot.communityId — which
  // may be a phase, community, etc.), then walks its full ancestor chain so the nearest level with
  // an address wins. The lot can be opened for editing from ANY ancestor (division/community/phase),
  // and an ancestor's own chain skips the in-between levels — so we never derive the address from the
  // URL location. Reuse the hierarchy already fetched for locationId only when it IS the lot's parent;
  // otherwise fetch the lot's own chain. CFG-2266.
  const urlIsLotParent = lot?.communityId === locationId;
  const { data: lotParentHierarchy } = useLocationHierarchy(builderId, urlIsLotParent ? "" : lot?.communityId ?? "");

  if (locationDataLoading || lotRowDataLoading) {
    return <Loading />;
  }

  assertIsNotUndefined(locationData);
  assertIsNotUndefined(lot);

  const locationName = locationData.location.name;
  const displayPrices = shouldDisplayPricing(
    "communityPlans",
    locationData.location,
    locationHierarchy?.ancestors,
    locationData.builder,
  );

  const addressHierarchy = urlIsLotParent ? locationHierarchy : lotParentHierarchy;
  const inheritedAddress = addressHierarchy
    ? getEffectiveLocationAddress([addressHierarchy.location, ...addressHierarchy.ancestors])
    : null;

  const breadcrumbs = [
    {
      text: locationName,
      href: locationHomePlansPath(builderId, locationId),
    },
    {
      text: "Lots",
      href: locationLotsPath(builderId, locationId),
    },
  ];

  return (
    <LotDetailsContext.Provider
      value={{
        builderId,
        community: locationData.location,
        inheritedAddress,
        displayPrices,
        lot,
      }}
    >
      <BuilderPortalLayout
        selected="locations"
        pageTitle={locationName}
        header={`Edit Lot ${getLotNumberDisplay(lot)}`}
        breadcrumbs={breadcrumbs}
      >
        <div className="builder-portal-layout-content">
          <main className={styles.lotDetailsContainer}>
            <LotDetailsForm />
          </main>
        </div>
      </BuilderPortalLayout>
    </LotDetailsContext.Provider>
  );
}

export const Route = createFileRoute("/builders/$builder_id/locations/$location_id/lots/$lot_id/")({
  component: withAuthentication(LotPage, { permissions: ["VIEW_LOTS"] }),
});
